import { FC } from 'react'
import { useOutletContext } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ContentList, ContentListItem, Heading, HeadingLevel, HeadingTag } from '@enbw/ui-react'
import styles from './styles.module.scss'
import { ContractCreatorType, ContractType, Salutation } from '@/domain/contract/types/contract'
import { valueOrEmpty } from '@/util/string-util'
import { formatDate, FormattedDateMode } from '@/util/date-util'
import { translateCountryCode } from '@/util/i18n-util'
import { CardLayout } from '@/components/CardLayout'
import { ContractPageOutletContext } from '@/domain/contract/layouts/ContractDetailsLayout/ContractDetailsLayout'
import { usePageTitle } from '@/application/routing/hooks/usePageTitle'

type MasterDataItem = {
  label: string
  value: string
  fullRow?: boolean
}

type MasterDataSection = {
  key: 'keyFigures' | 'contractDetails' | 'contractPartner' | 'billingAddress'
  title: string
  items: MasterDataItem[]
}

export const ContractMasterDataPage: FC = () => {
  const { contract } = useOutletContext<ContractPageOutletContext>()
  const { t } = useTranslation()

  usePageTitle(t('contract.masterData.pageTitle', { lastName: contract.masterData.contractPartner.lastName }))

  const getContractTypeLabel = (type: ContractType): string => {
    switch (type) {
      case ContractType.B2C:
        return t('contract.masterData.contractDetails.values.contractType.b2c')
      case ContractType.B2B:
        return t('contract.masterData.contractDetails.values.contractType.b2b')
      case ContractType.UNSPECIFIED:
        return t('contract.masterData.contractDetails.values.contractType.unspecified')
    }
  }

  const getCreatedByLabel = (createdByType: ContractCreatorType): string => {
    switch (createdByType) {
      case ContractCreatorType.CUSTOMER:
        return t('contract.masterData.contractDetails.values.createdBy.customer')
      case ContractCreatorType.SUPPORT:
        return t('contract.masterData.contractDetails.values.createdBy.support')
      case ContractCreatorType.SYSTEM:
        return t('contract.masterData.contractDetails.values.createdBy.system')
    }
  }

  const getSalutationLabel = (salutation: Salutation): string => {
    switch (salutation) {
      case Salutation.MR:
        return t('contract.masterData.contractPartner.values.salutation.mr')
      case Salutation.MS:
        return t('contract.masterData.contractPartner.values.salutation.ms')
      case Salutation.MX:
        return t('contract.masterData.contractPartner.values.salutation.mx')
      case Salutation.UNKNOWN:
        return t('contract.masterData.contractPartner.values.salutation.unknown')
    }
  }

  const { keyFigures, contractDetails, contractPartner, billingAddress } = contract.masterData

  const sections: MasterDataSection[] = [
    {
      key: 'keyFigures',
      title: t('contract.masterData.keyFigures.title'),
      items: [
        { label: t('contract.masterData.keyFigures.fields.emaId'), value: valueOrEmpty(keyFigures.emaId) },
        { label: t('contract.masterData.keyFigures.fields.billingAccountIdExternal'), value: valueOrEmpty(keyFigures.billingAccountIdExternal) }
      ]
    },
    {
      key: 'contractDetails',
      title: t('contract.masterData.contractDetails.title'),
      items: [
        { label: t('contract.masterData.contractDetails.fields.contractNumber'), value: valueOrEmpty(contractDetails.contractNumber) },
        { label: t('contract.masterData.contractDetails.fields.contractType'), value: getContractTypeLabel(contractDetails.contractType) },
        { label: t('contract.masterData.contractDetails.fields.createdAt'), value: formatDate(contractDetails.createdAt, FormattedDateMode.Date) },
        { label: t('contract.masterData.contractDetails.fields.createdBy'), value: getCreatedByLabel(contractDetails.createdByType) }
      ]
    },
    {
      key: 'contractPartner',
      title: t('contract.masterData.contractPartner.title'),
      items: [
        { label: t('contract.masterData.contractPartner.fields.salutation'), value: getSalutationLabel(contractPartner.salutation), fullRow: true },
        { label: t('contract.masterData.contractPartner.fields.firstName'), value: valueOrEmpty(contractPartner.firstName) },
        { label: t('contract.masterData.contractPartner.fields.lastName'), value: valueOrEmpty(contractPartner.lastName) },
        { label: t('contract.masterData.contractPartner.fields.birthday'), value: formatDate(contractPartner.birthday, FormattedDateMode.Date), fullRow: true },
        { label: t('contract.masterData.contractPartner.fields.email'), value: valueOrEmpty(contractPartner.email), fullRow: true },
        { label: t('contract.masterData.contractPartner.fields.phoneNumber'), value: valueOrEmpty(contractPartner.phoneNumber) },
        { label: t('contract.masterData.contractPartner.fields.alternativePhoneNumber'), value: valueOrEmpty(contractPartner.alternativePhoneNumber) }
      ]
    },
    {
      key: 'billingAddress',
      title: t('contract.masterData.billingAddress.title'),
      items: [
        { label: t('contract.masterData.billingAddress.fields.street'), value: valueOrEmpty(billingAddress.street) },
        { label: t('contract.masterData.billingAddress.fields.houseNumber'), value: valueOrEmpty(billingAddress.houseNumber) },
        { label: t('contract.masterData.billingAddress.fields.postalCode'), value: valueOrEmpty(billingAddress.postalCode) },
        { label: t('contract.masterData.billingAddress.fields.city'), value: valueOrEmpty(billingAddress.city) },
        { label: t('contract.masterData.billingAddress.fields.country'), value: translateCountryCode(billingAddress.country) }
      ]
    }
  ]

  return (
    <section className={styles.contractMasterDataPage}>
      <Heading level={HeadingLevel.H2} tag={HeadingTag.H2}>
        {t('contract.masterData.title')}
      </Heading>

      <div className={styles.cardsGrid}>
        {sections.map((section) => (
          <CardLayout
            key={section.title}
            padded
            title={
              <Heading level={HeadingLevel.H3} tag={HeadingTag.H3}>
                {section.title}
              </Heading>
            }
          >
            <ContentList>
              <div className={styles.itemsGrid}>
                {section.items.map((item) => (
                  <div key={`${section.title}-${item.label}`} className={item.fullRow ? styles.fullRowItem : undefined}>
                    <ContentListItem label={item.label} body={item.value} />
                  </div>
                ))}
              </div>
            </ContentList>
          </CardLayout>
        ))}
      </div>
    </section>
  )
}
